import React, { useRef, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import PaginatedTable from '@/components/ui/paginated';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getSubscriptions } from '@/services/api/subscriptions';
import { getDateFormat } from '@/util';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import Label from '@/components/Label';
import { Card, CardContent } from '@/components/ui/card';
import SubscriptionDialog from '@/components/Modal/SubscriptionModal';

export interface Props {
  userId: string;
}

const UserSubscriptionsTab: React.FC<Props> = ({ userId }) => {
  const reloadRef = useRef<() => void>();
  const [showActive, setShowActive] = useState<boolean>(false);
  const [modalState, setModalState] = useState<{
    open: boolean;
    mode: 'create' | 'edit';
    data?: {
      id: string;
      title: string;
      includedAmount: number;
      remainingAmount: number;
      paid: boolean;
    };
  }>({
    open: false,
    mode: 'create'
  });

  const handleSuccess = (action: 'added' | 'updated') => {
    setModalState({ open: false, mode: 'create' });
    toast.success(`Subscription ${action} successfully`);
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <div className="flex justify-between">
          <div className="flex items-center space-x-2">
            <Switch id="active-only" checked={showActive} onCheckedChange={setShowActive} />
            <Label htmlFor="active-only">Show active subscriptions only</Label>
          </div>

          <SubscriptionDialog
            open={modalState.open && modalState.mode === 'create'}
            onOpenChange={(open) => setModalState({ open, mode: 'create' })}
            userId={userId}
            mode="create"
            onSuccess={() => handleSuccess('added')}
            trigger={<Button variant="outline">Add new</Button>}
          />
        </div>

        <PaginatedTable
          key={JSON.stringify(showActive)}
          source={(page, size) => getSubscriptions(userId, showActive, page, size)}
          renderHeader={() => (
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-[100px]">Remaining</TableHead>
                <TableHead className="w-[120px]">Purchase date</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
          )}
        >
          {(subscriptions) => (
            <TableBody>
              {subscriptions.map((subscription) => (
                <TableRow key={subscription.id}>
                  <TableCell>
                    <PaidTag paid={subscription.paid} />
                  </TableCell>
                  <TableCell>{subscription.title}</TableCell>
                  <TableCell>
                    {subscription.remainingAmount} / {subscription.includedAmount}
                  </TableCell>
                  <TableCell>{getDateFormat(subscription.createdAt.toString())}</TableCell>
                  <TableCell>
                    <SubscriptionDialog
                      open={modalState.open && modalState.mode === 'edit' && modalState.data?.id === subscription.id}
                      onOpenChange={(open) =>
                        setModalState({
                          open,
                          mode: 'edit',
                          data: open ? subscription : undefined
                        })
                      }
                      mode="edit"
                      userId={userId}
                      initialData={subscription}
                      onSuccess={() => handleSuccess('updated')}
                      trigger={<Button variant="link">Edit</Button>}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </PaginatedTable>
      </CardContent>
    </Card>
  );
};

function PaidTag({ paid }) {
  return <Badge variant={paid ? 'success' : 'destructive'}>{paid ? 'Paid' : 'Failed'}</Badge>;
}

export default UserSubscriptionsTab;
